import React, { useState, useMemo, useEffect } from 'react';
import doc from './packages/components/assets/images/doc.svg.js';
import folder from './packages/components/assets/images/folder.svg.js';
import up from './packages/components/assets/images/up.svg.js';
import { useButtonStyles } from './hooks/useButtonStyles.js';
import { FieldLabel } from './FieldLabel.js';
import { FieldWrap } from './FieldWrap.js';
import { Icon } from './Icon.js';
import { Paragraph } from './Paragraph.js';

function bytesToSize(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0)
        return `0${sizes[0]}`;
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
    if (i === 0)
        return `${bytes} ${sizes[i]}`;
    return `${(bytes / Math.pow(1024, i)).toFixed(1)}${sizes[i]}`;
}
function getFileError(type, options) {
    switch (type) {
        case 'fileLimitMax':
            return { type, message: `Please select up to ${options.maxFiles} files` };
        case 'fileLimitMin':
            return {
                type,
                message: `Please select at least ${options.minFiles} files`,
            };
        case 'fileRead':
            return { type, message: 'Error reading file' };
        case 'fileSize':
            return { type, message: `File ${options.name} is too large` };
        case 'reading':
            return { type, message: 'An error occurred reading your files' };
        default:
            return { type, message: 'Error uploading file' };
    }
}
function getFileData(file, data) {
    return {
        file,
        previewSrc: data,
    };
}
function readFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(getFileError('fileRead'));
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(getFileData(file, reader.result));
            }
            else {
                reject(getFileError('fileRead'));
            }
        };
        reader.readAsDataURL(file);
    });
}
function validateFile(file, maxSize) {
    if (!file || file.size > maxSize * 1024 * 1024) {
        return false;
    }
    return true;
}
const FileField = React.forwardRef(({ accept, className = '', disabled, helpText, iconUrl, id, invalid, label, maxFiles, maxSize = 5, minFiles, multiple, name, onBlur, onChange, placeholder = 'Upload file', previewFiles, setError, setValue, value, ...props }, ref) => {
    const [files, setFiles] = useState();
    const listFull = useMemo(() => {
        if (maxFiles && files && files.length >= maxFiles) {
            return true;
        }
        return false;
    }, [files, maxFiles]);
    const buttonStyles = useButtonStyles({
        filled: true,
        rounded: true,
        variant: listFull ? 'dark' : 'secondary',
    });
    useEffect(() => {
        async function parseFiles(urls) {
            try {
                const filesData = await Promise.all(urls.map(async (url) => {
                    const { pathname } = new URL(url);
                    const name = pathname.substring(pathname.lastIndexOf('/') + 1, pathname.length) || pathname;
                    const extension = pathname.lastIndexOf('.') > -1
                        ? pathname.substring(pathname.lastIndexOf('.') + 1, pathname.length)
                        : null;
                    const response = await fetch(url);
                    const data = await response.blob();
                    return await readFile(new File([data], name, {
                        type: extension || response.headers.get('content-type'),
                    }));
                }));
                setFiles(filesData);
            }
            catch (error) {
                console.log(error);
                setError(name, getFileError('reading'));
            }
        }
        if (value) {
            parseFiles(Array.isArray(value) ? value : [value]);
        }
    }, []);
    async function updateFile(event) {
        event.preventDefault();
        try {
            const filesList = event.target.files;
            const filesTotal = filesList.length;
            if (!filesList.length) {
                return;
            }
            if (maxFiles && maxFiles < filesTotal) {
                throw getFileError('fileLimitMax', { maxFiles });
            }
            if (minFiles && minFiles > filesTotal) {
                throw getFileError('fileLimitMin', { minFiles });
            }
            let filesData = await Promise.all(Array.from(filesList).map(async (file) => {
                if (!validateFile(file, maxSize)) {
                    throw getFileError('fileSize', { name: file.name });
                }
                return await readFile(file);
            }));
            filesData = filesData.filter(item => item);
            if (multiple && files) {
                filesData = [...files, ...filesData];
            }
            updateValues(filesData);
        }
        catch (error) {
            updateValues(multiple ? [] : null, false);
            setError(name, error, { shouldFocus: true });
        }
    }
    function updateValues(data, shouldValidate = true) {
        // Remove `previewSrc` from files so it doesn't show up of form data
        const filesData = data.map(({ previewSrc, file }) => file);
        setFiles(data);
        setValue(name, filesData, { shouldValidate });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, className: [
                'grid p-4 bg-lilac-50 border-dashed border-2 rounded-2xl',
                invalid ? 'border-red' : 'border-lilac-300',
                listFull ? 'pointer-events-none' : '',
                className,
            ].join(' ') },
            React.createElement("div", { className: "p-4" },
                React.createElement("img", { src: iconUrl || folder, className: "w-11 mb-3" }),
                React.createElement(FieldLabel, { ...{ id, label, labelVisible: true } }),
                React.createElement(Paragraph, { className: "text-lilac-500" }, helpText)),
            React.createElement("div", { className: `${buttonStyles} relative select-none` },
                React.createElement("input", { ...{
                        ...(multiple ? { multiple } : {}),
                        ...(accept ? { accept: accept.join(',') } : {}),
                        ...props,
                        id,
                        disabled: listFull ? true : disabled,
                    }, type: "file", className: "cursor-pointer absolute w-full h-full opacity-0 top-0 left-0", onChange: updateFile }),
                React.createElement("img", { src: up, className: ['w-6', listFull ? 'hidden' : ''].join(' ') }),
                React.createElement("span", { "aria-hidden": "true" }, listFull
                    ? `Uploaded ${maxFiles} of ${maxFiles} files`
                    : placeholder))),
        files && files.length > 0 && (React.createElement("ul", { "aria-hidden": "true", className: "grid gap-4" }, files.map(({ previewSrc, file: { name, size, type } }, key) => {
            const isImage = type.match(/(jpg|jpeg|png|gif|svg)$/);
            const showPreview = isImage && previewFiles;
            const sizeLabel = bytesToSize(size);
            return (React.createElement("li", { className: "grid grid-cols-2 gap-2 items-center text-sm", key: key },
                showPreview && (React.createElement("img", { src: previewSrc, className: "object-cover w-full max-h-52 col-span-full" })),
                React.createElement("div", { className: "grid grid-cols-2-auto gap-x-2 items-center" },
                    !showPreview && (React.createElement(React.Fragment, null,
                        React.createElement("img", { src: doc, className: "w-9 row-span-2" }),
                        React.createElement("span", { className: "leading-tight font-medium block whitespace-nowrap overflow-hidden overflow-ellipsis" }, name))),
                    React.createElement("span", { className: "text-gray-500 text-xs" }, sizeLabel)),
                React.createElement("button", { type: "button", onClick: () => updateValues(files.filter((_, i) => i !== key)), className: "flex items-center text-red hover:text-red-700 justify-self-end" },
                    React.createElement(Icon, { type: "negative", className: "mr-1" }),
                    React.createElement("span", { className: "underline" }, "delete"))));
        })))));
});
const FileFieldControlled = ({ accept, helpText, label, maxFiles, maxSize, multiple, placeholder, theme, ...props }) => (React.createElement(FieldWrap, { ...{ handleErrors: true, handleValue: true, theme, ...props } },
    React.createElement(FileField, { ...{
            accept,
            helpText,
            label,
            maxFiles,
            maxSize,
            multiple,
            placeholder,
        } })));

export { FileFieldControlled };
//# sourceMappingURL=FileField.js.map
